import { CSSProperties } from "react";
import {
  ACTIONS_HEIGHT,
  LABEL_GAP,
  LABEL_HEIGHT,
  POPUP_BORDER,
  POPUP_PADDING,
  SEPARATOR_WIDTH,
} from "./constants";

/*
 * Single source of truth for the popup geometry: `useTimePickerPopup` measures
 * the popup with these functions and `TimePickerPopup` hands the very same
 * numbers to the CSS module as custom properties. Nothing else may derive a size
 * from the raw constants, otherwise the measured height and the rendered height
 * can drift apart.
 */

/** Everything the popup renders around the option lists. */
export const getPopupChromeHeight = (withActions: boolean): number =>
  POPUP_BORDER * 2 +
  POPUP_PADDING * 2 +
  LABEL_HEIGHT +
  LABEL_GAP +
  (withActions ? ACTIONS_HEIGHT : 0);

/** Height of one option list, i.e. the scrollable part of a column. */
export const getColumnHeight = (
  visibleItems: number,
  itemHeight: number,
): number => visibleItems * itemHeight;

/** Full popup height for a given amount of visible options. */
export const getPopupHeight = (
  visibleItems: number,
  itemHeight: number,
  withActions: boolean,
): number =>
  getPopupChromeHeight(withActions) + getColumnHeight(visibleItems, itemHeight);

/** Width the popup cannot shrink below: every column plus the ":" separators. */
export const getPopupMinWidth = (
  segmentCount: number,
  columnWidth: number,
): number =>
  POPUP_BORDER * 2 +
  POPUP_PADDING * 2 +
  segmentCount * columnWidth +
  Math.max(0, segmentCount - 1) * SEPARATOR_WIDTH;

interface PopupCssVariablesParams {
  itemHeight: number;
  visibleItems: number;
  columnWidth: number;
}

/** The same geometry, in the form the CSS module renders from. */
export const getPopupCssVariables = ({
  itemHeight,
  visibleItems,
  columnWidth,
}: PopupCssVariablesParams): CSSProperties => {
  const columnHeight = getColumnHeight(visibleItems, itemHeight);

  const variables: CSSProperties = {
    ["--ctp-item-height" as string]: `${itemHeight}px`,
    ["--ctp-column-height" as string]: `${columnHeight}px`,
    // half of the free space, so the first and last option can reach the middle
    ["--ctp-column-pad" as string]: `${(columnHeight - itemHeight) / 2}px`,
    ["--ctp-column-width" as string]: `${columnWidth}px`,
    ["--ctp-separator-width" as string]: `${SEPARATOR_WIDTH}px`,
    ["--ctp-label-height" as string]: `${LABEL_HEIGHT}px`,
    ["--ctp-label-gap" as string]: `${LABEL_GAP}px`,
    ["--ctp-popup-padding" as string]: `${POPUP_PADDING}px`,
    ["--ctp-actions-height" as string]: `${ACTIONS_HEIGHT}px`,
    ["--ctp-popup-border" as string]: `${POPUP_BORDER}px`,
  };

  return variables;
};
